import React, { act, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import instance from '../api/client';
import authService from '../authentication';
import { toast } from 'react-toastify';
import Loader from '../components/loaders/Loader';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import YearsSelector from '../components/YearsSelector';
import AppContext from '../contexts/AppContext';
import MitigationModal from '../components/MitigationModal';
import { faSignOutAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

/**
 * ✅ Beautiful color palette (cycled per diagram)
 */
const DIAGRAM_COLORS = [
  '#6366F1', // Indigo
  '#22C55E', // Emerald
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#06B6D4', // Cyan
  '#A855F7', // Purple
  '#EC4899', // Pink
];

const MitigationEffectsSimulation = () => {
    const { state } = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const projectCode = state?.project?.code;
    const pModel = state?.project;
    const [isSubmitting, setSubmitting] = useState(false);
     const [selectedDiagram, setSelectedDiagram] = useState(null);
    // const [selectedImpact, setSelectedImpact] = useState(null);
    const [selectedImpactId, setSelectedImpactId] = useState(null);
    const [numberOfYears, setNumberOfYears] = useState(1);
    const [showModal, setShowModal] = useState(false);
    const [view, setView] = useState("chart");
    const { openInputModal, openModal, modalInputValue } = useContext(AppContext)
    

      // START OF MITIGATION BARRIER STUFF
    const ITEMS_PER_PAGE = 3;
    const [currentPage, setCurrentPage] = useState(0);
    const [checkedState, setCheckedState] = useState({});
    const [radioState, setRadioState] = useState({});
    const [selectedMBarrierId, setSelectedMBarrierId] = useState();
    const [errors, setErrors] = useState({});
    const startIndex = currentPage * ITEMS_PER_PAGE;
    const currentItems = pModel?.schema.slice(
            startIndex,
            startIndex + ITEMS_PER_PAGE
        );

    const validatePage = () => {    
            const newErrors = {}
            currentItems.forEach((s, index) => {
                const globalIndex = startIndex + index;
                if (checkedState[globalIndex]) {
                    const eff = radioState[`effectiveness-${globalIndex}`];
                    const conf = radioState[`confidence-${globalIndex}`];
                    if (!eff || !conf){
                        newErrors[globalIndex] = 'Please select both effectiveness and confidence.'      
                    }
                }}
            )
            setErrors(newErrors);
            return Object.keys(newErrors).length === 0;
        }

    const submitMitigationEffectHandler = async () => {
        if (!validatePage()) return;
        try {
            setSubmitting(true)
            const effect = [];
            Object.keys(checkedState).forEach(key => {
            if(checkedState[key]){
                const index = Number(key)
                const criteriaName = pModel.schema[index].criteria;
                const level = radioState[`effectiveness-${index}`];
                const confidence = radioState[`confidence-${index}`];
                if (criteriaName && level && confidence) {
                    effect.push({
                        criteriaName: criteriaName,
                        levelOfImpact: level,
                        confidence: confidence,
                    })}
                
            }})
            
            const { data, status } = await instance.post(`/bowtie/update-mBarrier/${selectedMBarrier._id}/${selectedMBarrier.impactID}/${state.diagram.diagramId}`, effect, {
                headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${authService.getCurrentUser()}`
                }
            })
            if (status == 200) {
                toast.success("Submission recorded", {
                position: toast.POSITION.TOP_CENTER
                });
                console.log("selected mbarrier", selectedMBarrier._id)
                // setSelectedMBarrier(null);
                setShowModal(false);

            } else {
                toast.error("Submission Failed", {
                position: toast.POSITION.TOP_CENTER
                });
            }
        } catch (error) {
            toast.error(error, {
                position: toast.POSITION.TOP_CENTER
            });
        }
        setSubmitting(false)
    };

    // END OF MITIGATION BARRIER STUFF


   


    const { data: rawImpacts = [], isLoading } = useQuery({
    queryKey: ['all-impacts', state.project.code],
    queryFn: async () => {
        const { data } = await instance.get(
        `/prioritize/get-all-impactsForSimulation/${state.project.code}`,
        {
            headers: {
            Authorization: `Bearer ${authService.getCurrentUser()}`,
            },
        }
        );
        console.log('Fetched impacts for simulation:', data);
        return data;
    },
    staleTime: 5 * 60 * 1000, // ✅ 5 minutes fresh
    cacheTime: 30 * 60 * 1000, // ✅ 30 minutes in memory
    refetchOnWindowFocus: false,
    });
    const loading = isLoading;

    const selectedImpact = useMemo(() => {
        if (!selectedImpactId || !rawImpacts?.length) return null;
        return rawImpacts.find(i => i._id === selectedImpactId) || null;
    }, [rawImpacts, selectedImpactId]);

    const selectedMBarrier = useMemo(() => {
        if (!selectedMBarrierId || !rawImpacts?.length) return null;

        for (const impact of rawImpacts) {
            const found = impact.mbarriers?.find(b => b._id === selectedMBarrierId);
            if (found) return found;
        }

        return null;
    }, [rawImpacts, selectedMBarrierId]);


    useEffect(() => {
        if (!selectedMBarrier?.effect) return;
        console.log('Selected MBarrier text:', selectedMBarrier.data.text);
        console.log('Selected MBarrier effect:', selectedMBarrier.effect);

        const newCheckedState = {};
        const newRadioState = {};

        selectedMBarrier.effect.forEach((eff) => {
            const schemaIdx = pModel?.schema.findIndex((s) => s.criteria === eff.criteriaName);
            if (schemaIdx !== -1) {
            newCheckedState[schemaIdx] = true;
            newRadioState[`effectiveness-${schemaIdx}`] = eff.levelOfImpact;
            newRadioState[`confidence-${schemaIdx}`] = eff.confidence;
            }
        });

        setCheckedState(newCheckedState);
        setRadioState(newRadioState);
        setErrors({});
        setCurrentPage(0);

    }, [selectedMBarrier, pModel]);


    
    useEffect(() => {
    // ... EventSource initialization ...
        if (!projectCode) return;

            // Use either WebSocket or SSE connection depending on your final server choice
            // --- USING SSE (as per your last request) ---
        const sse = new EventSource('https://api.rafce.com/api/v1/sse/simulation-impacts/stream');
        sse.onmessage = e => {
            try {
                // Received data is now a single AllImpactsResponse object
                const updatedImpact = JSON.parse(e.data);
                console.log('[SSE] Received updated impact:', updatedImpact);
                console.log('[SSE] Current mbarriers in impact:', updatedImpact?.mbarriers);
                // --- KEY STEP: Functional Cache Update ---
                queryClient.setQueryData(
                    ['all-impacts', projectCode], 
                    (oldImpacts) => {
                        if (!oldImpacts) return []; // If cache is empty
                        
                        // Find the index of the impact that was updated
                        const index = oldImpacts.findIndex(i => i._id === updatedImpact._id);
                        
                        if (index !== -1) {
                            // Create a NEW array with the updated object replacing the old one
                            const newImpacts = [...oldImpacts];
                            newImpacts[index] = updatedImpact;
                            return newImpacts; // Return the new array
                        }
                        
                        // If the updated impact wasn't found (shouldn't happen here), return the old array
                        return oldImpacts;
                    }
                );

                console.log(`[SSE] Cache updated for single impact ID: ${updatedImpact._id}`);
                toast(`Impacts updated in real-time!`, {
                        position: toast.POSITION.TOP_CENTER, 
                        autoClose: 1500
                    });
            } catch (error) {
                console.error("[SSE ERROR] Failed to parse JSON or update cache:", error);
            }
        sse.onerror = (e) => {
            console.error("[SSE] Error occurred:", e);
            sse.close();
        };

        return () => {
            console.log("[SSE] Cleaning up connection.");
            sse.close();
        };
    };

    // ... cleanup ...
    }, [queryClient, projectCode]);

  /**
   * ✅ GROUP IMPACTS BY DIAGRAM NAME
   */
    const groupedByDiagram = useMemo(() => {
        return rawImpacts.reduce((acc, item) => {
        if (!acc[item.diagramName]) acc[item.diagramName] = [];
        acc[item.diagramName].push(item);
        return acc;
        }, {});
    }, [rawImpacts]);

    
  /**
   * ✅ ASSIGN A UNIQUE COLOR PER DIAGRAM
   */
    const diagramColors = useMemo(() => {
        const map = {};
        Object.keys(groupedByDiagram).forEach((name, index) => {
        map[name] = DIAGRAM_COLORS[index % DIAGRAM_COLORS.length];
        });
        return map;
    }, [groupedByDiagram]);

    const diagramNames = Object.keys(groupedByDiagram);
    const impactsForSelectedDiagram =
        groupedByDiagram[selectedDiagram] || [];
    const mitigationsForSelectedImpact =
        selectedImpact?.mbarriers || [];

    /**
     * ✅ CHART DATA FROM impact.data
     */
   
    const chartData = useMemo(() => {
        // gets the original criteria score dictionary from the database
        // {"criteria1": score1, "criteria2": score2, ...}
        const originalScore = selectedImpact?.original_score || {};

        // gets the updated dictionary after the mitigation have been applied
        const cumulativeEffect = selectedImpact?.cumulative_effect || {};

        // [criteria1, criteria2, ...]
        const criteriaKeys = Object.keys(originalScore);

        if (!criteriaKeys.length) return [];

        const data = [];

        // Year 0 = Original Total Score
        let prevYearScores = { ...originalScore };
        const sumScores = scores => Object.values(scores).reduce((a,b)=>a+b, 0);

        console.log('Cummulative Effect:', cumulativeEffect);
        data.push({
            name: "Base year",
            value: sumScores(prevYearScores)+1
        });

        // Year 1 → N
        for (let year = 1; year <= numberOfYears; year++) {
            const currentYearScores = {};
            for (const key of criteriaKeys) {
                if (key in cumulativeEffect) {
                    currentYearScores[key] = Math.max(0, prevYearScores[key] * cumulativeEffect[key]
                );
                } else {
                currentYearScores[key] = prevYearScores[key]; // No change if not in cumulativeEffect
                }
            }


            data.push({
            name: `Year ${year}`,
            value: sumScores(currentYearScores)+1
            });

            prevYearScores = currentYearScores;
        }

        return data;
    }, [selectedImpact, numberOfYears]);


    const activeColor = diagramColors[selectedDiagram];

    return (
        
        <>
                            
        

        {loading ? (
            <div className="absolute z-50 flex w-full h-full items-center justify-center bg-black/40 backdrop-blur-sm">
            <Loader animationName={'collab'} height={300} width={400} />
            </div>
        ) : (
            <>
            {showModal && (
                    <MitigationModal
                        barrierName={selectedMBarrier ? selectedMBarrier.data.text : ''}
                        setShowMBarrierModal={setShowModal}
                        pModel={pModel}
                        ITEMS_PER_PAGE={ITEMS_PER_PAGE}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        currentItems={currentItems}
                        startIndex={startIndex}
                        checkedState={checkedState}
                        setCheckedState={setCheckedState}
                        errors={errors}
                        radioState={radioState}
                        setRadioState={setRadioState}
                        validatePage={validatePage}
                        submitMitigationEffectHandler={submitMitigationEffectHandler}
                    />                                            
                )}
            <div style={{ fontFamily: 'MavenPro' }} className="w-full h-[100vh] bg-gradient-to-br from-gray-900 to-gray-800 text-white flex flex-col p-12 gap-4">
                <div onClick={() => {
                    openModal("Exit?",
                    `Make sure everything is saved before you exit. Are you sure you want to exit? `,
                    () => { navigate(-1) }
                    )}} data-tooltip-content={'Exit'} data-tooltip-id='exit' className=' absolute top-0 left-0 hover:scale-110  px-5 py-1 cursor-pointer border border-red-700 hover:border-red-800 shadow-lg text-white hover:bg-red-800 duration-200 rounded-lg bg-red-700 text-black spacing-2'>
                    <FontAwesomeIcon icon={faSignOutAlt} /> Exit
                </div>
            {/* ✅ HEADER */}
            <div>
                <div className='text-3xl uppercase text-gray-100 text-center font-semibold '>Mitigation Effectiveness Simulation</div>
                <div className='w-32 h-[2px] bg-gray-100 mx-auto mb-5 mt-1' />
            </div>

            
                <div className="flex flex-col justify-between ">
                    {/* SCROLLABLE DIAGRAM ROW */}
                    <div className="overflow-x-auto ">
                        <div className="flex justify-center items-center gap-4 min-w-max p-2">
                            {diagramNames.map((name) => (
                            <button
                                key={name}
                                onClick={() => {
                                
                                setSelectedDiagram(name);
                                setSelectedImpactId(null);
                                }}
                                style={{
                                backgroundColor:
                                    selectedDiagram === name
                                    ? diagramColors[name]
                                    : '#374151',
                                }}
                                className="flex-shrink-0 px-6 py-3 rounded-md font-semibold text-lg capitalize whitespace-nowrap transition-all duration-200 hover:scale-105 shadow-md"
                            >
                                {name}
                            </button>
                            ))}
                        </div>
                    </div>

                    {/* MAIN BODY */}
                    <div className="flex flex-1 gap-6 mt-2">
                        {/* IMPACT LIST */}
                        <div style={{ borderColor: activeColor }} className="w-1/3  bg-gray-900 border-2 border-slate-100 rounded-md p-5 shadow-lg  ">
                            <h2 className="font-bold text-xl mb-4">
                                Impacts for{" "}
                                <span style={{ color: activeColor }}>
                                {selectedDiagram}
                                </span>
                            </h2>
                            <div className="overflow-y-auto max-h-[300px] pr-2 scroll-smooth scrollbar-thin scrollbar-thumb-purple-400">
                                {impactsForSelectedDiagram.map((impact) => (
                                    <div
                                    key={impact.id}
                                    onClick={() => {setSelectedImpactId(impact._id); console.log('selectedImpact:', impact);}}
                                    className={`p-4 mb-3 rounded-xl cursor-pointer transition-all shadow ${
                                        selectedImpact?._id === impact._id
                                        ? 'scale-[1.02]'
                                        : 'hover:scale-[1.01]'
                                    }`}
                                    style={{
                                        backgroundColor:
                                        selectedImpact?._id === impact._id
                                            ? activeColor
                                            : '#374151',
                                    }}
                                    >
                                    <div className="font-semibold">
                                        {impact.data.text}
                                    </div>
                                    <div className="text-sm opacity-90 ">
                                        Score: {impact.score.toFixed(2)}
                                        {/* {impact.scoreAfterMitigation !== undefined ? ` | After a Year: ${impact.scoreAfterMitigation.toFixed(2)}` : ''} */}
                                    </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/*  CHART PANEL */}
                        
                        <div style={{borderColor:activeColor}} className="w-2/3 bg-gray-900 rounded-2xl border-2 p-6 shadow-lg flex flex-col">
                            {/* Toggle Buttons */}
                            <div className="flex mb-4 space-x-4 items-center justify-center">
                                <button
                                    className={`px-4 py-2 rounded ${
                                    view === "chart" ? "bg-blue-600 text-white" : "bg-gray-700 text-gray-300"
                                    }`}
                                    onClick={() => setView("chart")}
                                >
                                    Chart View
                                </button>
                               
                                    
                                <button
                                    className={`px-4 py-2 rounded ${
                                    view === "mitigation" ? "bg-blue-600 text-white" : "bg-gray-700 text-gray-300"
                                    }`}
                                    onClick={() => setView("mitigation")}
                                >
                                    Mitigation View
                                </button>
                            </div>
                             {/* Dynamic Heading */}
                            <h2 className="font-bold text-lg mb-4">
                            {view === "chart" ? "Trend Analysis" : "Mitigation Factors"}
                            </h2>

                            {/* Content */}
                            {!selectedImpact ? (
                            <div className="flex flex-1 items-center justify-center text-gray-400">
                                Select an impact to view details
                            </div>
                            ) : view === "chart" ? (
                                <div className='flex flex-col justify-between gap-8'>
                                    <div className="h-4 flex gap-2 items-center">
                                        <span>See effect over duration:</span>
                                        <YearsSelector 
                                         numberOfYears = {numberOfYears} 
                                         setNumberOfYears={setNumberOfYears}
                                        />   
                                    </div>
                                    <div>
                                    {chartData.length === 0 ? (
                                        <div className="flex flex-1 items-center justify-center text-gray-400">
                                        No trend data available
                                        </div>
                                    ) : (
                                        <ResponsiveContainer width="100%" height={420}>
                                        <LineChart data={chartData}>
                                            <XAxis dataKey="name" stroke="#CBD5F5" />
                                            <YAxis stroke="#CBD5F5" />
                                            <Tooltip
                                            contentStyle={{
                                                backgroundColor: "#111827",
                                                borderRadius: 10,
                                                border: "none",
                                                color: "white",
                                            }}
                                            />
                                            <Line
                                            type="monotone"
                                            dataKey="value"
                                            stroke={activeColor}
                                            strokeWidth={3}
                                            dot={{ r: 4 }}
                                            />
                                        </LineChart>
                                        </ResponsiveContainer>
                                    )}
                                    </div>
                                </div>
                            ) : (
                            // Mitigation Factors View
                            <>
                            <div className="flex flex-col space-y-2 overflow-y-auto max-h-[300px] pr-2">
                                {mitigationsForSelectedImpact.length > 0 ? (
                                mitigationsForSelectedImpact.map((barrier) => (
                                    <div
                                    key={barrier._id}
                                    className="flex justify-between p-3 bg-gray-800 rounded hover:bg-gray-700 transition"
                                    >
                                    <div className="font-semibold">
                                        {barrier.data.text}
                                    </div>
                                    <button className="px-3 py-1 bg-blue-600 rounded text-white hover:bg-blue-500 transition"
                                        onClick={() => {setSelectedMBarrierId(barrier._id); setShowModal(true); }}>
                                        Edit
                                    </button>
                                    </div>
                                ))
                                ) : (
                                <div className="flex flex-1 items-center justify-center text-gray-400">
                                    No mitigation factors available
                                </div>
                                )}
                            </div>
                           
                            </>
                            )
                            
                            }
                            
                        </div>
                                            
                    </div>
                </div>
            </div>
            </>
        )}
        </>
    );
};

export default MitigationEffectsSimulation;

